import {
  ArrowCta,
  FormCard,
  Heading,
  Text,
  Checkbox,
  HStack,
  VStack,
} from 'components/ui';
import { useToken } from '@chakra-ui/react';
import HeaderNav from '../HeaderNav/HeaderNav';
import { StickyCtaLayout } from '../ui/StickyCtaLayout';
import { useAuthenticatedUser } from '../../api/user/useAuthenticatedUser';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import SignUpInfoAffiliateLookup from '../OnboardingForm/SignUpInfoAffiliateLookup';
import { RequiredTerms } from '../EligibilityView/components/RequiredTerms';
import { FullBenefitsModal } from '../EligibilityView/FullBenefitsModal';
import { useHealthPlanForm } from './hooks/useHealthPlanForm';
import { useEffect, useState } from 'react';
import useUserRegisterPut from '../../api/user/mutations/useUserRegisterPut';
import useRegisterPost from '../../api/register/mutations/useRegisterPost';
import { useAffiliate } from '../../api/affiliates/useAffiliate';
import { useTracking } from '../../hooks/useTracking';
import { useReferrer } from '../../hooks/useReferrer';
import { useSignUpIdentity } from '../../hooks/useSignUpIdentity';
import { AffiliateType } from '../../models/affiliate';
import {
  TRACKING_HEALTH_PLAN_COLLECTION,
  TRACKING_HEALTH_PLAN_SKIPPED,
} from '../OnboardingForm/constants';
import { CheckIcon } from 'lucide-react';

/*
  Dear future reader, this file contains the logic which handles how
  an affiliate and referrer is set for the register endpoint. Most
  of this data is pulled from ReactQuery, and stored via formData with
  a dispatch. This is because we need to maintain selections made by the user
  so we can more easily pass them to the register endpoint. I'd prefer to not store
  so much in the formData, but due to how our overall onboarding flow is built, this was
  necessary.

  The health plan input value is stored in formData.affiliate_name so EndOfFlow can
  access it and determine if the form is valid for submission.

  None if this is as clean as I'd like it to be, and for that, I apologize!
*/

export const HealthPlan = () => {
  const { track } = useTracking();
  const [checkIconColor] = useToken('colors', ['secondary.500']);

  const { search, state } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showFullRelease, setShowFullRelease] = useState(false);
  const [isSkippingInsurance, setIsSkippingInsurance] = useState(false);
  const { mutation: updateUserRegistrationMutation } = useUserRegisterPut();
  const { mutation: registerMutation } = useRegisterPost();
  const { user: authenticatedUser } = useAuthenticatedUser();

  const utmSource = searchParams.get('utm_source');

  const { affiliate: existingAffiliate, isLoading: isFetchingAffiliate } =
    useAffiliate({
      affiliateId: utmSource || authenticatedUser?.referrer,
    });

  const [requiredTerms, setRequiredTerms] = useState({
    auth_medical_release: false,
    auth_payer_to_pay_ayble: false,
  });

  const {
    formData,
    isLoading,
    error,
    setError,
    setIsLoading,
    updateAffiliate,
    setAffiliateName,
    handleAffilSelection,
    updateReferrer,
    clearAffiliate,
  } = useHealthPlanForm();

  const isSamlConnection = !!useReferrer()?.is_saml;

  const signUpIdentity = useSignUpIdentity();

  const userId = authenticatedUser?.id || signUpIdentity.userId || '';

  const email =
    signUpIdentity.email || formData.email || authenticatedUser?.email || '';

  const showCostTransparency =
    !isSkippingInsurance &&
    (existingAffiliate?.cost_transparency_body ||
      existingAffiliate?.cost_transparency_header);

  useEffect(() => {
    track(TRACKING_HEALTH_PLAN_COLLECTION, null);
  }, [track]);

  useEffect(() => {
    // redirect to location view if utm_source is a SAML affiliate
    if (isSamlConnection)
      navigate(`/location${search}`, {
        state: {
          ...state,
          userId,
        },
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSamlConnection]);

  useEffect(() => {
    if (!existingAffiliate && isFetchingAffiliate) return;

    // If there is a referrer from the url (utm_source), we:
    //   1. Fetch referrer Affiliate by utm_source - referrerFromUrl
    //   2. Set referrerFromUrl.utm_source as referrer
    //   3. Set referrerFromUrl as default affiliate - may be changed using health plan dropdown
    if (existingAffiliate) {
      // update referrer in formData if it's different from urm_source
      if (formData.referrer !== existingAffiliate.utm_source) {
        updateReferrer(existingAffiliate.utm_source);
      }

      // only pre-select referrer if it is a health plan
      if (existingAffiliate.affiliate_type === AffiliateType.healthPlan) {
        // check presence of selected_affiliate/affiliate_name properties to see if
        // user has 'cleared' health plan input. If so, do not default/pre-select

        // pre-select referrer utm_source as affiliate
        if (!formData.hasOwnProperty('selected_affiliate')) {
          updateAffiliate(existingAffiliate.utm_source);
        }

        // default referrer name as health plan input value
        if (!formData.hasOwnProperty('affiliate_name')) {
          setAffiliateName(existingAffiliate.name);
        }
      }
    }

    // disabling deps because we truly only want this to run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchingAffiliate]);

  const handleSaveInsuranceInfo = async (affiliateValue?: string | null) => {
    setIsLoading(true);
    setError('');

    try {
      if (authenticatedUser) {
        // null must be sent explicitly so PATCH clears a previously saved affiliate
        await updateUserRegistrationMutation.mutateAsync({
          affiliate: affiliateValue ?? null,
        });
      } else {
        if (!email) {
          setError(
            'We could not find your details. Please start the sign up again.'
          );
          return;
        }

        // Include affiliate when set or explicitly null (clear). Omit only if undefined.
        const payload: { email: string; affiliate?: string | null } = {
          email,
        };
        if (affiliateValue !== undefined) {
          payload.affiliate = affiliateValue;
        }
        await registerMutation.mutateAsync(payload);
      }

      navigate(`/location${search}`, {
        state: {
          affiliate: affiliateValue,
          referrer: formData.referrer,
          email,
          userId,
          firstName: signUpIdentity.firstName,
          lastName: signUpIdentity.lastName,
        },
      });
    } catch (error) {
      setError('Failed to process your request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipInsurance = async () => {
    // A skipped plan and a plan never chosen look the same on the user, so the
    // funnel needs to be told the difference.
    track(TRACKING_HEALTH_PLAN_SKIPPED, null);
    clearAffiliate();
    // Send null (not omit) so BE clears any previously saved affiliate and
    // subscription-path can route to Stripe instead of eligibility.
    await handleSaveInsuranceInfo(null);
  };

  const handleNext = async () => {
    if (isSkippingInsurance) {
      await handleSkipInsurance();
      return;
    }
    await handleSaveInsuranceInfo(formData.selected_affiliate);
  };

  const isFormValid = () => {
    if (isSkippingInsurance) return true;

    return (
      requiredTerms.auth_medical_release &&
      requiredTerms.auth_payer_to_pay_ayble &&
      !!formData.selected_affiliate
    );
  };

  const ErrorDisplay = () =>
    error ? (
      <Text color="error.500" textAlign="center" fontSize="14px">
        {error}
      </Text>
    ) : null;

  const ButtonArea = () => {
    return (
      <ArrowCta
        onClick={handleNext}
        disabled={!isFormValid() || isLoading}
        loading={isLoading}
        alignSelf={'center'}
      >
        Next
      </ArrowCta>
    );
  };

  const skipInsuranceCheckboxLabel = () => {
    const discount = Math.round(Number(existingAffiliate?.discount));
    const hasDiscount = discount > 0 && existingAffiliate?.discount_code;
    const isEmployerWithDiscount =
      existingAffiliate?.affiliate_type === AffiliateType.employer &&
      hasDiscount;

    if (!isEmployerWithDiscount) {
      return <Text as="span">I don't want to use insurance</Text>;
    }

    return (
      <Text as="span">
        I'm not enrolled in a health plan sponsored by {existingAffiliate.name}{' '}
        -{' '}
        <Text as="span" fontWeight="bold">
          {`I’d like to use my ${discount}% off code ${existingAffiliate.discount_code} instead`}
        </Text>
      </Text>
    );
  };

  const renderContent = () => {
    return (
      <>
        <Heading mb={5} textStyle="heading.h2">
          Connect your health plan
        </Heading>

        <FormCard maxW="formWidth" mx="auto">
          <SignUpInfoAffiliateLookup
            affiliateName={formData.affiliate_name}
            onAffilSelection={(affiliate) => {
              setIsSkippingInsurance(false);

              handleAffilSelection({
                utm_source: affiliate?.utm_source,
                name: affiliate?.name,
              });
            }}
            onClear={() => {
              setAffiliateName('');
              updateAffiliate(null);
            }}
          />

          <HStack alignItems="center" gap="10px" width="100%">
            <Checkbox
              _checked={{
                backgroundColor: 'secondary.200',
                borderColor: 'secondary.200',
              }}
              bg="white"
              borderColor="#9FB0B8"
              borderWidth="1.5px"
              isChecked={isSkippingInsurance}
              onChange={() => {
                setAffiliateName('');
                updateAffiliate(null);
                setIsSkippingInsurance((s) => !s);
              }}
              padding="1px"
              rounded="5px"
              value="1"
              flexShrink={0}
              data-testid="skip-insurance-checkbox"
            />

            <Text
              color="secondary.500"
              textStyle="body.check"
              textAlign="left"
              flex={1}
            >
              {skipInsuranceCheckboxLabel()}
            </Text>
          </HStack>

          {showCostTransparency && (
            <VStack bg={'blue.100'} p={4} gap={4} borderRadius={'xl'} mt={4}>
              <VStack gap={2} alignItems={'stretch'}>
                <HStack gap={3} alignItems={'center'}>
                  <CheckIcon
                    size={18}
                    strokeWidth={3}
                    style={{
                      flexShrink: 0,
                      color: checkIconColor,
                    }}
                  />
                  <Text
                    fontSize={14.5}
                    fontWeight={'bold'}
                    color={'secondary.500'}
                  >
                    {existingAffiliate?.cost_transparency_header}
                  </Text>
                </HStack>
                <VStack pl={7} gap={2}>
                  {existingAffiliate?.cost_transparency_body
                    ?.split('\n')
                    .filter(Boolean)
                    .map((paragraph, index) => (
                      <Text key={index} color={'#0E3E46'} fontSize={13}>
                        {paragraph}
                      </Text>
                    ))}
                </VStack>
              </VStack>
            </VStack>
          )}

          {!isSkippingInsurance && (
            <RequiredTerms
              formData={requiredTerms}
              isDisabled={isSkippingInsurance}
              setData={setRequiredTerms}
              setShowFullRelease={setShowFullRelease}
            />
          )}

          <ErrorDisplay />
        </FormCard>
      </>
    );
  };

  return (
    <>
      <StickyCtaLayout
        actionArea={<ButtonArea />}
        header={<HeaderNav />}
        maxWidth="formWidth"
      >
        {renderContent()}
      </StickyCtaLayout>

      <FullBenefitsModal
        isOpen={showFullRelease}
        onClose={() => setShowFullRelease(false)}
      />
    </>
  );
};
